import { useState, useRef, useEffect } from 'react';

/**
 *  @typedef {Object} UseAutosizeVideosReturn
 *  @property {any} embedRef the ref for the actual embedded item
 *  @property {any} embedContainer the ref for the container of the embedded item
 */

/**
 * useAutosizeVideos - a hook used to autosize an embedded video
 *
 * @param {string} embedCode The string of the embedcode, used so that the useEffect hook changes if this does
 * @param {number} customRatio If this is provided, this is how the video is scaled.
 * @return {UseAutosizeVideosReturn} The return value of useAutosizeVideos, an object with both refs needed
 */

const useAutosizeVideos = (embedCode, customRatio) => {
  const [ratio, setRatio] = useState(customRatio || 0);
  const embedRef = useRef(null);
  const embedContainer = useRef(null);

  const updateRatio = () => {
    if (embedRef && embedRef.current) {
      const { width, height } = embedRef.current.getBoundingClientRect();
      if (!customRatio) setRatio(height / width);
    }
  };

  const setSize = () => {
    if (embedRef && embedContainer && embedContainer.current) {
      const { width: containerWidth } =
        embedContainer.current.getBoundingClientRect();
      if (embedRef.current && embedRef.current.firstChild) {
        embedRef.current.firstChild.width = containerWidth;
        embedRef.current.firstChild.height = ratio * containerWidth;
      }
    }
  };

  useEffect(() => {
    window.addEventListener('resize', setSize);
    return () => {
      window.removeEventListener('resize', setSize);
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    updateRatio();
    setSize();
    //eslint-disable-next-line
  }, [embedRef, embedContainer, embedCode]);

  return { embedRef, embedContainer };
};

export default useAutosizeVideos;
